import { Plus, X } from "lucide-react";
import { VariantProps, cva } from "class-variance-authority";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LanguageCombobox } from "@/components/language/language-combobox";
import { LanguageProficiencySelector } from "@/components/common/proficiency-select";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { SupportedLanguages } from "@/components/language/supported-languages";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export type LanguageLearningEntry = {
  id: string;
  languageCode: string;
  proficiency: string;
  learningObjectives: string[];
};

export const LanguageLearningObjectivesFormFields = {
  learnLanguages: z
    .array(
      z.object({
        id: z.string(),
        languageCode: z
          .string()
          .min(
            1,
            "pages:learningLanguageObjectives.languageLearning.languageInvalid",
          ),
        proficiency: z
          .string()
          .min(
            1,
            "pages:learningLanguageObjectives.languageLearning.proficiencyInvalid",
          ),
        learningObjectives: z.array(z.string()),
      }),
    )
    .min(1, "pages:learningLanguageObjectives.min1Entry")
    .max(5, "pages:learningLanguageObjectives.maxEntries"),
};

export const LanguageLearningObjectivesFormSchema = z.object(
  LanguageLearningObjectivesFormFields,
);
export type LanguageLearningObjectivesFormValues = z.infer<
  typeof LanguageLearningObjectivesFormSchema
>;

const sizeVariants = cva("overflow-auto pr-2", {
  variants: {
    size: {
      sm: "max-h-80 md:max-h-72 2xl:max-h-[700px]",
      lg: "max-h-120 md:max-h-96 2xl:max-h-[300px]",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

interface InteractionTableProps extends VariantProps<typeof sizeVariants> {
  mode?: "progresive" | "minimal" | "default";
  isNoDelete?: boolean;
}

export default function LanguageLearningObjectivesForm({
  mode = "default",
  isNoDelete = false,
  size = "sm",
}: InteractionTableProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    control,
    formState: { errors },
    getValues,
    clearErrors,
  } = useFormContext<LanguageLearningObjectivesFormValues>();

  useEffect(() => {
    console.log("Errors:", errors);
  }, [errors]);

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "learnLanguages",
  });

  const availableObjectives = ["travel", "work", "academic", "personal"];

  const addLanguageEntry = () => {
    if (fields.length >= 5) {
      setErrorMessage(t("pages:learningLanguageObjectives.maxEntries"));
      return;
    }
    const currentValues = getValues("learnLanguages");
    if (currentValues[currentValues.length - 1]?.languageCode === "") {
      setErrorMessage(
        t("pages:learningLanguageObjectives.fillCurrentEntryFirst"),
      );
      return;
    }
    setErrorMessage(null);
    append({
      id: "new_" + Date.now().toString(),
      languageCode: "",
      proficiency: "",
      learningObjectives: [],
    });
  };

  const removeLanguageEntry = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const updateLanguageEntry = (
    index: number,
    field: keyof LanguageLearningEntry,
    value: string,
  ) => {
    const currentValues = getValues("learnLanguages");
    const currentEntry = currentValues[index];
    update(index, { ...currentEntry, [field]: value });
    clearErrors("learnLanguages");
    setErrorMessage(null);
  };

  const toggleObjective = (index: number, objective: string) => {
    const currentValues = getValues("learnLanguages");
    const currentEntry = currentValues[index];
    const objectives = currentEntry.learningObjectives.includes(objective)
      ? currentEntry.learningObjectives.filter(
          (obj: string) => obj !== objective,
        )
      : [...currentEntry.learningObjectives, objective];

    update(index, { ...currentEntry, learningObjectives: objectives });
  };

  const getLanguagesNotSelected = (currentIndex: number) => {
    const currentValues = getValues("learnLanguages");
    const selectedLanguages = currentValues
      .filter((_: unknown, index: number) => index !== currentIndex)
      .map((entry: LanguageLearningEntry) => entry.languageCode);

    return SupportedLanguages.filter(
      (lang) => !selectedLanguages.includes(lang.key),
    );
  };

  return (
    <div className="col-span-3">
      <ScrollArea className={sizeVariants({ size })}>
        <div className="space-y-4 pb-4 mb-4">
          <div className="space-y-4">
            {fields.map((field, index) => {
              // Get the actual values for this field
              const currentValues = getValues("learnLanguages");
              const entry = currentValues[index];

              return (
                <div
                  key={field.id}
                  className="rounded-2xl border px-4 pb-4 shadow-sm border-primary"
                >
                  {isNoDelete && !entry.id.startsWith("new_") ? (
                    <div className="mt-4" />
                  ) : (
                    <div className="flex items-start justify-end">
                      {fields.length > 1 ? (
                        <Button
                          type="button"
                          onClick={() => removeLanguageEntry(index)}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 rounded-full p-0 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <div className="pt-4" />
                      )}
                    </div>
                  )}

                  <div className="flex flex-row gap-2 flex-wrap" id={entry.id}>
                    <div className="space-y-2 col-span-1 flex-grow">
                      {mode !== "minimal" && (
                        <Label className="hidden sm:inline">
                          {t(
                            "pages:learningLanguageObjectives.languageLearning.language",
                          )}
                          *
                        </Label>
                      )}
                      {entry.id.startsWith("new_") ? (
                        <LanguageCombobox
                          value={entry?.languageCode || ""}
                          onValueChange={(value) =>
                            updateLanguageEntry(index, "languageCode", value)
                          }
                          isError={
                            !!errors?.learnLanguages?.[index]?.languageCode
                          }
                          languagesAvailable={getLanguagesNotSelected(index)}
                        />
                      ) : (
                        <div className="-space-y-1">
                          <span className="inline-block font-bold text-primary">
                            {t("common:languages." + entry.languageCode)}
                          </span>
                          <span className="hidden sm:block text-sm font-light">
                            {SupportedLanguages.find(
                              (lang) => lang.key === entry.languageCode,
                            )?.label || ""}
                          </span>
                        </div>
                      )}
                      {errors?.learnLanguages?.[index]?.languageCode && (
                        <p className="text-sm text-destructive">
                          {t(
                            errors.learnLanguages[index].languageCode.message ||
                              "",
                          )}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1 flex-grow">
                      {mode !== "minimal" && (
                        <Label>
                          {t(
                            "pages:learningLanguageObjectives.languageLearning.proficiency",
                          )}
                          *
                        </Label>
                      )}
                      <LanguageProficiencySelector
                        value={entry?.proficiency || ""}
                        isError={!!errors?.learnLanguages?.[index]?.proficiency}
                        setValue={(value) =>
                          updateLanguageEntry(index, "proficiency", value)
                        }
                      />
                      {errors?.learnLanguages?.[index]?.proficiency && (
                        <p className="text-sm text-destructive">
                          {t(
                            errors.learnLanguages[index].proficiency.message ||
                              "",
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 mt-4">
                    <div className="space-y-2 flex flex-col md:flex-row md:items-center justify-end md:gap-4">
                      {mode !== "minimal" && (
                        <Label className="hidden sm:inline">
                          {t(
                            "pages:learningLanguageObjectives.languageLearning.objectives",
                          )}
                        </Label>
                      )}
                      <div className="flex flex-wrap gap-2 col-span-1">
                        {availableObjectives.map((objective) => {
                          const isSelected =
                            entry?.learningObjectives?.includes(objective);
                          return (
                            <Button
                              key={objective}
                              type="button"
                              variant={isSelected ? "default" : "ghost"}
                              onClick={() => toggleObjective(index, objective)}
                              className={`rounded-full border border-primary px-2 py-1 text-sm font-light transition-colors h-7`}
                            >
                              {t(
                                `pages:learningLanguageObjectives.languageLearning.${objective}`,
                              )}
                            </Button>
                          );
                        })}
                      </div>
                      {errors?.learnLanguages?.[index]?.learningObjectives && (
                        <p className="text-sm text-red-500">
                          {
                            errors.learnLanguages[index].learningObjectives
                              .message
                          }
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col items-center justify-center">
            {errorMessage && (
              <div className="text-destructive text-sm mb-2 text-center">
                {errorMessage}
              </div>
            )}

            <Button
              type="button"
              onClick={addLanguageEntry}
              size="sm"
              className="h-9 gap-1 rounded-full bg-teal-500 hover:bg-teal-600"
            >
              <Plus className="h-4 w-4" />
              {t("pages:learningLanguageObjectives.languageLearning.addEntry")}
            </Button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
